import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './staff-design-system.css';
import './Staff.css';
import { SectionHeader } from '../../components/staff/ui/SectionHeader';
import { KPICard } from '../../components/staff/ui/KPICard';
import { EventEditSheet } from '../../components/staff/EventEditSheet';
import { eventTypeConfig, type AgendaEvent } from '../../components/staff/eventTypes';
import { StaffLoadError, type StaffLoadErrorKind } from '../../components/staff/StaffLoadError';
import { apiFetch } from '../../utils/api';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

type DayCA = { caTTC: number; tickets: number; couverts: number };

type Cloture = {
  totaux: { ttc: number; ht: number; tva: number };
  reglements: { methodId: string; method: string; amount: number; count: number }[];
  ventilationTVA: { rate: number; base: number; amount: number; ttc: number }[];
  controle: { equilibre: boolean; ecartTtcVsReglements: number };
};

const eur2 = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Décale une date "YYYY-MM-DD" de n jours (midi → insensible aux fuseaux). */
function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return fmtDate(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Classe un mode de règlement Cashpad en CB / Espèces / Autre (libellé). */
function payClass(method: string): 'cb' | 'cash' | 'other' {
  const m = method.toLowerCase();
  if (/esp[èe]ce|cash|liquide/.test(m)) return 'cash';
  if (/cb|carte|visa|mastercard|amex|adyen|tap/.test(m)) return 'cb';
  return 'other';
}

/** Plage horaire lisible pour une carte événement (ex. "19:00 → 23:00", "Toute la journée"). */
function eventTimeLabel(ev: AgendaEvent): string {
  const sameDay = !ev.dateFin || ev.dateFin === ev.dateDebut;
  if (!ev.heureDebut && !ev.heureFin) return sameDay ? 'Toute la journée' : 'Sur plusieurs jours';
  const start = ev.heureDebut ?? '';
  const endTime = ev.heureFin ?? '';
  const endPrefix = sameDay ? '' : '+1j ';
  if (start && endTime) return `${start} → ${endPrefix}${endTime}`;
  if (start) return `dès ${start}`;
  return `jusqu'à ${endPrefix}${endTime}`;
}

/** Admin gate : lit alterrego_user.isAdmin (peuplé depuis /auth). Jamais l'email. */
function isAdminUser(): boolean {
  try {
    const raw = localStorage.getItem('alterrego_user');
    return raw ? JSON.parse(raw).isAdmin === true : false;
  } catch {
    return false;
  }
}

export function StaffAgenda() {
  const navigate = useNavigate();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [salesData, setSalesData] = useState<Record<string, DayCA>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StaffLoadErrorKind | null>(null);
  const [cloture, setCloture] = useState<Cloture | null>(null);
  const [prevWeekCA, setPrevWeekCA] = useState<number | null>(null);
  const [dayEvents, setDayEvents] = useState<AgendaEvent[]>([]);
  // sheet === undefined → fermé ; sheet === null → création ; sheet === AgendaEvent → édition.
  const [sheet, setSheet] = useState<AgendaEvent | null | undefined>(undefined);
  const admin = isAdminUser();

  // (Re)charge les événements du jour sélectionné.
  const loadDayEvents = useCallback((date: string) => {
    apiFetch(`/events?from=${date}&to=${date}`, { auth: false })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setDayEvents(Array.isArray(data) ? data : []))
      .catch(() => setDayEvents([]));
  }, []);

  useEffect(() => {
    if (!selectedDate) { setDayEvents([]); return; }
    loadDayEvents(selectedDate);
  }, [selectedDate, loadDayEvents]);

  useEffect(() => {
    const month = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    setLoading(true);
    setError(null);
    let cancelled = false;
    apiFetch(`/pos/sales-by-month?month=${month}`)
      .then((r) => {
        if (r.status === 401) { if (!cancelled) setError('auth'); return null; }
        if (!r.ok) { if (!cancelled) setError('error'); return null; }
        return r.json();
      })
      .then((d) => { if (!cancelled && d) setSalesData(d); })
      .catch(() => { if (!cancelled) setError('error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewYear, viewMonth]);

  // Détail du Z (clôture) du jour sélectionné + CA du même jour S-1 (comparatif).
  useEffect(() => {
    if (!selectedDate) { setCloture(null); setPrevWeekCA(null); return; }
    let cancelled = false;
    setCloture(null);
    setPrevWeekCA(null);
    apiFetch(`/pos/cloture/by-date/${selectedDate}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && !d.error) setCloture(d as Cloture); })
      .catch(() => {});
    // CA du jour S-1 = SOMME de toutes les Z du jour (pas une seule) → comparatif
    // cohérent avec le CA affiché (issu de sales-by-month, lui aussi agrégé).
    apiFetch(`/pos/cloture/day-total/${addDays(selectedDate, -7)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && !d.error && typeof d.caTTC === 'number') setPrevWeekCA(d.caTTC); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedDate]);

  const first = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
  let startDay = first.getDay() - 1;
  if (startDay < 0) startDay = 6;
  const cells: (number | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthEntries = Object.entries(salesData);
  const monthTotal = monthEntries.reduce((s, [, v]) => s + v.caTTC, 0);
  const monthTickets = monthEntries.reduce((s, [, v]) => s + v.tickets, 0);
  const monthCouverts = monthEntries.reduce((s, [, v]) => s + v.couverts, 0);

  const maxDayCA = Math.max(...monthEntries.map(([, v]) => v.caTTC), 1);

  const selectedData: DayCA | null = selectedDate ? salesData[selectedDate] ?? null : null;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
    setSelectedDate(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
    setSelectedDate(null);
  };

  const todayStr = fmtDate(now.getFullYear(), now.getMonth(), now.getDate());

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>Agenda</h1>
        <span className="staff-badge-v2">{MONTHS[viewMonth].slice(0, 3)}. {viewYear}</span>
      </div>

      {/* Month nav */}
      <div className="staff-month-nav">
        <button onClick={prevMonth} aria-label="Mois précédent">‹</button>
        <span>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} aria-label="Mois suivant">›</button>
      </div>

      {/* Monthly summary KPI row */}
      <div className="kpi-quick-row">
        <KPICard variant="quick" label="CA TTC" value={`${monthTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`} />
        <KPICard variant="quick" label="Tickets" value={String(monthTickets || '—')} />
        <KPICard variant="quick" label="Panier moy." value={monthTickets > 0 ? `${(monthTotal / monthTickets).toFixed(0)} €` : '—'} />
      </div>

      {loading && <p className="staff-empty">Chargement…</p>}
      {error && <StaffLoadError kind={error} />}

      {/* Calendar */}
      {!loading && !error && (
        <div className="staff-cal-v2">
          <div className="staff-cal-header">
            {DAYS_SHORT.map((d) => <div key={d} className="staff-cal-header-cell">{d}</div>)}
          </div>
          {Array.from({ length: cells.length / 7 }, (_, w) => (
            <div key={w} className="staff-cal-row">
              {cells.slice(w * 7, w * 7 + 7).map((day, i) => {
                if (day === null) return <div key={i} className="staff-cal-cell empty" />;
                const dateStr = fmtDate(viewYear, viewMonth, day);
                const data = salesData[dateStr];
                const isSelected = dateStr === selectedDate;
                const isToday = dateStr === todayStr;
                const intensity = data ? Math.min(1, data.caTTC / maxDayCA) : 0;
                return (
                  <button
                    key={i}
                    className={`staff-cal-cell ${isSelected ? 'selected' : ''} ${data ? 'has-data' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => setSelectedDate(dateStr)}
                    style={data ? { '--cell-intensity': intensity } as React.CSSProperties : undefined}
                  >
                    <span className="staff-cal-day">{day}</span>
                    {data && (
                      <span className="staff-cal-ca">
                        {data.caTTC >= 1000 ? `${(data.caTTC / 1000).toFixed(1)}k` : Math.round(data.caTTC)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Selected day detail */}
      {selectedDate && selectedData && (
        <div className="staff-section-v2">
          <SectionHeader
            title={new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            caption="Service clôturé"
          />
          <div className="staff-day-detail-v2">
            <div className="staff-day-stat">
              <strong>{selectedData.caTTC.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</strong>
              <span>CA TTC</span>
              {prevWeekCA != null && prevWeekCA > 0 && (() => {
                const delta = (selectedData.caTTC - prevWeekCA) / prevWeekCA * 100;
                const up = delta >= 0;
                return (
                  <em className={`staff-day-delta ${up ? 'up' : 'down'}`}>
                    {up ? '▲' : '▼'} {Math.abs(delta).toFixed(0)} % <span>vs S-1</span>
                  </em>
                );
              })()}
            </div>
            <div className="staff-day-stat">
              <strong>{selectedData.tickets}</strong>
              <span>Tickets</span>
            </div>
            <div className="staff-day-stat">
              <strong>{selectedData.couverts}</strong>
              <span>Couverts</span>
            </div>
            <div className="staff-day-stat">
              <strong>{selectedData.tickets > 0 ? `${(selectedData.caTTC / selectedData.tickets).toFixed(0)} €` : '—'}</strong>
              <span>Panier moy.</span>
            </div>
          </div>

          {/* Détail du Z (clôture) : règlements par mode + ventilation TVA */}
          {cloture && (() => {
            const total = cloture.reglements.reduce((s, r) => s + r.amount, 0) || 1;
            const mix = { cb: 0, cash: 0, other: 0 };
            cloture.reglements.forEach((r) => { mix[payClass(r.method)] += r.amount; });
            const pct = (v: number) => Math.round((v / total) * 100);
            const tvaProv = cloture.ventilationTVA.reduce((s, t) => s + t.amount, 0);
            return (
            <div className="staff-z-detail">
              {/* Cockpit : mix paiement + TVA à provisionner */}
              <div className="staff-z-block">
                <div className="staff-z-title">Encaissements</div>
                <div className="staff-mix-bar" role="img" aria-label="Répartition des encaissements">
                  {mix.cb > 0 && <span className="staff-mix-seg cb" style={{ width: `${pct(mix.cb)}%` }} />}
                  {mix.cash > 0 && <span className="staff-mix-seg cash" style={{ width: `${pct(mix.cash)}%` }} />}
                  {mix.other > 0 && <span className="staff-mix-seg other" style={{ width: `${pct(mix.other)}%` }} />}
                </div>
                <div className="staff-mix-legend">
                  {mix.cb > 0 && <span><i className="cb" /> CB {pct(mix.cb)} %</span>}
                  {mix.cash > 0 && <span><i className="cash" /> Espèces {pct(mix.cash)} %</span>}
                  {mix.other > 0 && <span><i className="other" /> Autre {pct(mix.other)} %</span>}
                </div>
              </div>
              <div className="staff-z-block staff-z-provision">
                <div className="staff-z-line">
                  <span>💰 TVA à provisionner</span>
                  <span><strong>{eur2(tvaProv)} €</strong></span>
                </div>
              </div>
              <div className="staff-z-block">
                <div className="staff-z-title">Règlements</div>
                {cloture.reglements.map((r) => (
                  <div key={r.methodId} className="staff-z-line">
                    <span>{r.method}</span>
                    <span><strong>{eur2(r.amount)} €</strong> <em>· {r.count}</em></span>
                  </div>
                ))}
              </div>
              <div className="staff-z-block">
                <div className="staff-z-title">TVA</div>
                {cloture.ventilationTVA.map((t) => (
                  <div key={t.rate} className="staff-z-line">
                    <span>{t.rate} % <em>(HT {eur2(t.base)} €)</em></span>
                    <span><strong>{eur2(t.amount)} €</strong></span>
                  </div>
                ))}
                <div className="staff-z-line staff-z-total">
                  <span>HT total</span><span><strong>{eur2(cloture.totaux.ht)} €</strong></span>
                </div>
              </div>
              {!cloture.controle.equilibre && (
                <div className="staff-z-warn">⚠️ Écart règlements / TTC : {eur2(cloture.controle.ecartTtcVsReglements)} €</div>
              )}
            </div>
            );
          })()}
        </div>
      )}

      {/* Événements du jour — section dédiée (n'écrase pas le cockpit Z). */}
      {selectedDate && (
        <div className="staff-section-v2">
          <div className="staff-events-head">
            <SectionHeader title="Événements" />
            {admin && (
              <button
                type="button"
                className="staff-events-add"
                onClick={() => setSheet(null)}
                aria-label="Ajouter un événement"
              >
                +
              </button>
            )}
          </div>

          {dayEvents.length === 0 ? (
            <p className="staff-empty">Aucun événement ce jour</p>
          ) : (
            <div className="staff-events-list">
              {dayEvents.map((ev) => {
                const cfg = eventTypeConfig(ev.type);
                return (
                  <button
                    key={ev.id}
                    type="button"
                    className="staff-event-card"
                    onClick={() => admin && setSheet(ev)}
                    style={admin ? undefined : { cursor: 'default' }}
                  >
                    <div className="staff-event-cover" style={{ background: cfg.color }}>
                      {ev.coverImageUrl && <img src={ev.coverImageUrl} alt="" />}
                    </div>
                    <div className="staff-event-body">
                      <div className="staff-event-title">{ev.titre}</div>
                      <div className="staff-event-meta">
                        <span className="staff-event-badge" style={{ background: cfg.color }}>{cfg.label}</span>
                        {/* Sans ce badge, rien ne distingue ici un événement
                            visible des clients d'un brouillon invisible. */}
                        {!ev.published && <span className="staff-event-draft">Brouillon</span>}
                        <span className="staff-event-time">{eventTimeLabel(ev)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!loading && !error && monthEntries.length === 0 && (
        <p className="staff-empty">Aucun service clôturé sur ce mois</p>
      )}

      {sheet !== undefined && (
        <EventEditSheet
          event={sheet}
          defaultDate={selectedDate ?? todayStr}
          onClose={() => setSheet(undefined)}
          onSaved={(saved) => {
            // L'événement enregistré peut porter une AUTRE date que le jour
            // sélectionné (créer le 22 un événement du 31). Recharger le jour
            // sélectionné le ferait « disparaître » : enregistré, invisible,
            // sans message. On suit donc la date de l'événement — vue mois
            // comprise, sinon le jour surligné sort du calendrier affiché.
            const [y, m] = saved.dateDebut.split('-').map(Number);
            setViewYear(y);
            setViewMonth(m - 1);
            setSelectedDate(saved.dateDebut);
            // setSelectedDate ne déclenche rien si la date est inchangée (même
            // jour) : le rechargement explicite reste nécessaire pour voir la
            // modification.
            loadDayEvents(saved.dateDebut);
          }}
          onDeleted={() => { if (selectedDate) loadDayEvents(selectedDate); }}
        />
      )}
    </div>
  );
}
